import React, { useRef } from "react";
import { Box, Text, Flex, type FlexProps } from "@chakra-ui/react";
import { useInView } from "framer-motion";
import { Graph } from "./graph";
import { KeyContainer } from "./key-container";
import { KeyLabel } from "./key-label";
import { KeyValue } from "./key-value";
import { Percentage } from "./percentage";
import { KeyFooter } from "./key-footer";

export const borderSpacing = { base: "smaller", lg: "big", xl: "bigger" };
const animationDuration = 2.5;

type SplitsAnimationProps = { delay?: number; keyContainerProps?: FlexProps } & FlexProps;

export function SplitsAnimation({ delay = 1, keyContainerProps, ...flexProps }: SplitsAnimationProps) {
  const scope = useRef<HTMLDivElement>(null);
  const isInView = useInView(scope, { amount: 0.5 });
  const animationDelay = delay;

  return (
    <Flex ref={scope} boxSize="100%" pos="relative" {...flexProps}>
      <Flex align="center" boxSize="100%" justify="center" mr="big" mt="small">
        <Graph
          isInView={isInView}
          animationRoot={scope}
          // XX: Hacking timing to make the lines and text start at the same time, since part of the graph overlaps itself
          animationDuration={animationDuration + 0.2}
          animationDelay={animationDelay - 0.2}
        />
      </Flex>

      <KeyContainer pos="absolute" top={borderSpacing} right={0} {...keyContainerProps}>
        <Box>
          <KeyLabel color="brand.accent.primary">Holdout group</KeyLabel>
          <KeyValue
            isInView={isInView}
            animationDuration={animationDuration}
            animationDelay={animationDelay}
            valueStart={2800}
            valueEnd={5000}
          />
          <KeyFooter>
            <Text
              as="span"
              bg="product.gray.50"
              border="1px"
              borderColor="brand.border.extraLight"
              borderRadius="tiny"
              color="brand.textSecondary.light"
              fontSize="brand.2xs"
              fontWeight="semibold"
              py="tiniest"
              px="tinier"
            >
              Baseline
            </Text>
          </KeyFooter>
        </Box>

        <Box>
          <KeyLabel color="new.violet.700">Treatment</KeyLabel>
          <KeyValue
            isInView={isInView}
            animationDuration={animationDuration}
            animationDelay={animationDelay}
            valueStart={3300}
            valueEnd={9000}
          />
          <KeyFooter>
            <Percentage isInView={isInView} animationDelay={animationDelay} animationDuration={animationDuration} />
          </KeyFooter>
        </Box>
      </KeyContainer>
    </Flex>
  );
}
